import type { ReactNode } from 'react';

/**
 * Small Markdown renderer for the wiki.
 *
 * It exists in-repo because the dashboard carries no Markdown dependency and
 * `npm install` is blocked in this environment. It renders to React elements
 * rather than an HTML string, so there is no `dangerouslySetInnerHTML` path and
 * therefore no HTML injection surface at all — raw HTML in the source comes out
 * as literal text.
 *
 * Supported: ATX headings, paragraphs, unordered/ordered lists, fenced and
 * indented code blocks, blockquotes, horizontal rules, tables, and inline
 * strong/emphasis/code/link/image.
 */

/** Only these schemes may become a real link; anything else renders as text. */
const SAFE_SCHEME = /^(https?:|mailto:)/i;

function isSafeHref(href: string): boolean {
  const trimmed = href.trim();
  // Relative and anchor links stay in-document and carry no scheme risk.
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return true;
  if (!trimmed.includes(':')) return true;
  return SAFE_SCHEME.test(trimmed);
}

// --------------------------------------------------------------- inline

const INLINE = /(!?)\[([^\]]*)\]\(([^)\s]+)\)|(\*\*|__)(.+?)\4|(\*|_)(.+?)\6|`([^`]+)`/;

/**
 * Splits a line into React nodes. Recurses on the text around each match, so
 * nesting like **bold with `code`** works; the regex is deliberately
 * non-greedy so the leftmost shortest span wins.
 */
export function renderInline(text: string, keyPrefix = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;

  while (rest.length > 0) {
    const m = INLINE.exec(rest);
    if (!m || m.index === undefined) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const key = `${keyPrefix}-${n++}`;

    if (m[3] !== undefined) {
      const href = m[3];
      const label = m[2] ?? '';
      if (m[1] === '!') {
        out.push(isSafeHref(href)
          ? <img key={key} src={href} alt={label} className="max-w-full rounded border border-border my-2" />
          : <span key={key}>{m[0]}</span>);
      } else if (isSafeHref(href)) {
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer"
             className="text-primary hover:underline">
            {renderInline(label, key)}
          </a>,
        );
      } else {
        // e.g. javascript: — show the source, never make it clickable.
        out.push(<span key={key}>{m[0]}</span>);
      }
    } else if (m[5] !== undefined) {
      out.push(<strong key={key} className="font-semibold">{renderInline(m[5], key)}</strong>);
    } else if (m[7] !== undefined) {
      out.push(<em key={key}>{renderInline(m[7], key)}</em>);
    } else if (m[8] !== undefined) {
      out.push(
        <code key={key} className="px-1 py-0.5 rounded bg-surface-muted text-[0.85em] font-mono">
          {m[8]}
        </code>,
      );
    }

    rest = rest.slice(m.index + m[0].length);
  }

  return out;
}

// ---------------------------------------------------------------- blocks

const H = /^(#{1,6})\s+(.*)$/;
const UL = /^\s*[-*+]\s+(.*)$/;
const OL = /^\s*\d+[.)]\s+(.*)$/;
const HR = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const QUOTE = /^>\s?(.*)$/;
const FENCE = /^\s*```(.*)$/;
const TABLE_SEP = /^\s*\|?[\s:-]*-[\s:|-]*\|?\s*$/;

const HEADING_CLASS = [
  'text-2xl font-semibold mt-6 mb-3',
  'text-xl font-semibold mt-6 mb-3',
  'text-lg font-semibold mt-5 mb-2',
  'text-base font-semibold mt-4 mb-2',
  'text-sm font-semibold mt-4 mb-2',
  'text-sm font-semibold text-content-muted mt-4 mb-2',
];

function splitRow(line: string): string[] {
  return line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((s) => s.trim());
}

export function renderMarkdown(src: string): ReactNode[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  const k = () => `b-${key++}`;

  while (i < lines.length) {
    const line = lines[i]!;

    if (line.trim() === '') { i++; continue; }

    const fence = FENCE.exec(line);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !FENCE.test(lines[i]!)) body.push(lines[i]!), i++;
      i++; // closing fence (or EOF — an unclosed fence still renders)
      out.push(
        <pre key={k()} className="my-3 p-3 rounded-md bg-surface-sunken border border-border overflow-x-auto">
          <code className="text-xs font-mono text-content">{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Four-space indented code, but only when it is not a list continuation.
    if (/^ {4}\S/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && (/^ {4}/.test(lines[i]!) || lines[i]!.trim() === '')) {
        body.push(lines[i]!.slice(4));
        i++;
      }
      while (body.length > 0 && body[body.length - 1]!.trim() === '') body.pop();
      out.push(
        <pre key={k()} className="my-3 p-3 rounded-md bg-surface-sunken border border-border overflow-x-auto">
          <code className="text-xs font-mono text-content">{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    if (HR.test(line)) {
      out.push(<hr key={k()} className="my-6 border-border" />);
      i++;
      continue;
    }

    const h = H.exec(line);
    if (h) {
      const level = h[1]!.length;
      const Tag = `h${level}` as 'h1';
      out.push(<Tag key={k()} className={HEADING_CLASS[level - 1]}>{renderInline(h[2]!, k())}</Tag>);
      i++;
      continue;
    }

    // Table: a header row followed by a |---|---| separator.
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1]!) && lines[i + 1]!.includes('-')) {
      const head = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.includes('|') && lines[i]!.trim() !== '') {
        rows.push(splitRow(lines[i]!));
        i++;
      }
      out.push(
        <div key={k()} className="my-3 overflow-x-auto">
          <table className="min-w-full text-sm border border-border rounded-md">
            <thead className="bg-surface-muted">
              <tr>{head.map((cell, ci) => (
                <th key={ci} className="px-3 py-2 text-left font-semibold border-b border-border">
                  {renderInline(cell, `th-${ci}`)}
                </th>
              ))}</tr>
            </thead>
            <tbody>{rows.map((row, ri) => (
              <tr key={ri} className="border-b border-border last:border-0">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 align-top">{renderInline(cell, `td-${ri}-${ci}`)}</td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) {
        body.push(QUOTE.exec(lines[i]!)![1]!);
        i++;
      }
      out.push(
        <blockquote key={k()} className="my-3 pl-4 border-l-2 border-border text-content-muted">
          {renderMarkdown(body.join('\n'))}
        </blockquote>,
      );
      continue;
    }

    if (UL.test(line) || OL.test(line)) {
      const ordered = OL.test(line) && !UL.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? OL.test(lines[i]!) : UL.test(lines[i]!))) {
        const m = (ordered ? OL : UL).exec(lines[i]!)!;
        items.push(m[1]!);
        i++;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      out.push(
        <ListTag key={k()} className={`my-3 pl-6 space-y-1 ${ordered ? 'list-decimal' : 'list-disc'}`}>
          {items.map((it, idx) => <li key={idx}>{renderInline(it, `li-${idx}`)}</li>)}
        </ListTag>,
      );
      continue;
    }

    // Paragraph: consecutive plain lines, joined with soft breaks.
    const para: string[] = [];
    while (
      i < lines.length && lines[i]!.trim() !== ''
      && !H.test(lines[i]!) && !UL.test(lines[i]!) && !OL.test(lines[i]!)
      && !HR.test(lines[i]!) && !QUOTE.test(lines[i]!) && !FENCE.test(lines[i]!)
    ) {
      para.push(lines[i]!);
      i++;
    }
    out.push(
      <p key={k()} className="my-3 leading-relaxed">
        {para.map((l, idx) => (
          <span key={idx}>
            {idx > 0 && <br />}
            {renderInline(l, `p-${idx}`)}
          </span>
        ))}
      </p>,
    );
  }

  return out;
}

export function Markdown({ source, className = '' }: { source: string; className?: string }) {
  return <div className={`text-content ${className}`}>{renderMarkdown(source)}</div>;
}
